import React from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import styled from 'styled-components';

import { media } from 'styled-bootstrap-grid';
import useTranslation from 'next-translate/useTranslation';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { Track, style, Genre } from '../../api';

import { DotsMenu, ArrowDown, ArrowUp } from './Icons';
import { Menu, MenuItem } from './Menu';
import { TrackLink, ProfileLink, TrackEditLink, TrackReportLink } from './Link';
import { TextBold14, TextRegular14, TextRegular12, TextBold12 } from './Text';
import {
  useIsDesktop,
  desktop,
  noDesktop,
  mobile,
  Desktop,
} from '../../style/constants';

import Plays from './Plays';
import Flex from './Flex';
import Likes from './Likes';
import GenreLabel from './GenreLabel';
import PlayerCover from '../PlayerCover';
import TrackStatus from './TrackStatus';
import TrackVisibilityStatus from './TrackVisibilityStatus';
import LikeTrackButton from './Buttons/V2/LikeTrackButton';
import { usePlayerState } from '../Player/PlayerContext';
import {
  TrackProvider,
  TrackConsumer,
  TrackContextValue,
} from '../MyTracks/context/Track';

dayjs.extend(relativeTime);
interface Props {
  className?: string;
  track: Track;
  genre?: Genre;
  isProfilePage?: boolean;
  isCurrentUserProfilePage?: boolean;
  chartNumber?: number;
  playTrack: (trackId: number) => void;
  openShareModal: (trackId: number) => void;
}

const TrackLine = ({
  className,
  track,
  genre,
  chartNumber,
  playTrack,
  openShareModal,
  isProfilePage,
  isCurrentUserProfilePage,
}: Props) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'my-track-menu',
    disableAutoFocus: true,
  });

  const {
    id,
    user,
    title,
    total_likes,
    total_plays,
    position_last_week,
  } = track;

  const { t } = useTranslation('tracks');
  const { display_name, id: userId } = user;

  const isDesktop = useIsDesktop();
  const playerState = usePlayerState();
  const isPlaying = !playerState?.paused;

  return (
    <TrackProvider track={track}>
      <TrackConsumer>
        {({ onDelete, onActivate, isTrackOwner }: TrackContextValue) => (
          <>
            <Container className={className}>
              {!isProfilePage && (chartNumber || position_last_week) && (
                <PositionWrapper>
                  {chartNumber && <ChartNumber>{chartNumber}</ChartNumber>}
                  {position_last_week !== undefined && (
                    <Position>
                      {position_last_week === 'new' && (
                        <PositionNew>New</PositionNew>
                      )}
                      {position_last_week > 0 && (
                        <>
                          <IconArrowUp /> {position_last_week}
                        </>
                      )}
                      {position_last_week === 0 && <> - </>}
                      {position_last_week < 0 && (
                        <>
                          <IconArrowDown /> {position_last_week}
                        </>
                      )}
                    </Position>
                  )}
                </PositionWrapper>
              )}
              <PlayerCover
                isPlaying={isPlaying}
                track={track}
                size={isDesktop ? 90 : 50}
                playTrack={playTrack}
                circle
              />
              <InfoWrapper>
                <Flex flexDirection="column">
                  <Flex alignItems="center">
                    <TextBold14 style={{ marginRight: 8 }}>
                      <TrackLink trackId={id} className="text-dark">
                        {title || 'Untitled'}
                      </TrackLink>
                    </TextBold14>
                    <TextBold12>
                      {genre && <GenreLabel genreId={genre.id} />}
                    </TextBold12>
                  </Flex>
                  <ArtistAndGenre>
                    <ProfileLink profileId={userId}>{display_name}</ProfileLink>
                  </ArtistAndGenre>
                </Flex>
                <Flex alignItems="flex-end">
                  <Plays plays={total_plays} />
                  <Likes likes={total_likes} />
                </Flex>
              </InfoWrapper>
              <Right>
                <div className="right-content">
                  {isCurrentUserProfilePage && (
                    <Desktop>
                      <TrackStatus {...track} />
                      <TrackVisibilityStatus
                        {...track}
                        style={{ marginLeft: '10px' }}
                      />
                    </Desktop>
                  )}
                  <MoreMenuButton {...bindTrigger(popupState)}>
                    <DotsMenu />
                  </MoreMenuButton>
                  <Menu {...bindMenu(popupState)}>
                    {onActivate && (
                      <MenuItem
                        onClick={() => {
                          popupState.close();

                          onActivate();
                        }}
                      >
                        <span>{t('tracks:makeActive')}</span>
                      </MenuItem>
                    )}
                    {!isTrackOwner && (
                      <TrackReportLink trackId={track.id}>
                        <MenuItem>
                          <span>{t('tracks:report')}</span>
                        </MenuItem>
                      </TrackReportLink>
                    )}
                    <MenuItem
                      onClick={() => {
                        popupState.close();

                        openShareModal(track.id);
                      }}
                    >
                      <span>{t('tracks:shareThisVirpp')}</span>
                    </MenuItem>
                    {false && (
                      /**
                       * @todo - For some future flow upgrade
                       */
                      <MenuItem>
                        <span>{t('tracks:addToPlaylist')}</span>
                      </MenuItem>
                    )}
                    {isTrackOwner && (
                      <TrackEditLink trackId={track.id}>
                        <MenuItem>{t('tracks:edit')}</MenuItem>
                      </TrackEditLink>
                    )}
                    {onDelete && (
                      <MenuItem
                        onClick={() => {
                          popupState.close();

                          onDelete();
                        }}
                      >
                        <span style={{ color: style.error_new }}>
                          {t('tracks:delete')}
                        </span>
                      </MenuItem>
                    )}
                  </Menu>
                </div>
                <LikeTrackButton iconOnly size={20} trackId={track.id} />
                <CreatedAt>
                  {dayjs(track.created_at, 'YYYY-MM-DD HH:mm:ss').fromNow()}
                </CreatedAt>
              </Right>
            </Container>
          </>
        )}
      </TrackConsumer>
    </TrackProvider>
  );
};

const Container = styled.div``;

const Right = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  margin-right: 1rem;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;

  .right-content {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const CreatedAt = styled(TextRegular12)`
  color: ${style.grey_main_new};
  font-style: italic;
`;

export default styled(TrackLine)`
  width: 100%;
  display: flex;
  justify-content: flex-start;

  @media ${desktop} {
    height: 90px;
    align-items: center;
    .track-image {
      height: 100%;
      display: flex;
      align-items: center;
    }
  }

  @media ${noDesktop} {
    height: 72px;
    align-items: flex-start;
  }
`;

const PositionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  vertical-align: middle;

  @media ${mobile} {
    margin-right: 20px;
    margin-left: 10px;
  }
`;

const Position = styled.div`
  display: flex;
  font-size: 12px;
  align-items: center;
  color: ${style.grey_dark_new};
`;

const PositionNew = styled.div`
  color: ${style.instaColor};
`;

const IconArrowDown = styled(ArrowDown)`
  color: ${style.pink};
  height: 20px;
  margin-left: -10px;
`;

const IconArrowUp = styled(ArrowUp)`
  color: ${style.greenGradientDark};
  height: 20px;
  margin-left: -10px;
`;

const ChartNumber = styled.div`
  font-size: 30px;
  text-align: center;
  font-weight: 950;
  width: 60px;

  ${media.xs`
    min-width: 25px;
    width: auto;
    font-size: 20px;
  `}
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  justify-content: space-between;
  height: 100%;

  ${GenreLabel} {
    white-space: nowrap;
  }

  ${TextBold14} {
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media ${noDesktop} {
      max-width: 100px;
    }
  }

  ${Likes} {
    margin-left: 12px;
  }
`;

const ArtistAndGenre = styled(TextRegular14)`
  padding: 0px;
  color: ${style.grey_dark_new};
  margin-top: 6px;

  a {
    color: ${style.grey_dark_new};
  }
`;

const MoreMenuButton = styled.div`
  cursor: pointer;
  padding: 4px 4px;
  margin-right: -4px;
  margin-left: 10px;
  svg {
    width: 18px;
    fill: ${style.grey_main_new};
  }
`;
